import React, { useEffect, useState } from "react";
import {
  Check,
  X,
  Loader2,
  FileText,
  Receipt,
  CalendarClock,
  ListTodo,
  StickyNote,
  Tag,
  MessageSquare,
  Hash,
  Briefcase,
  Mail,
  Image as ImageIcon,
  Copy,
  ExternalLink,
  Link2,
} from "lucide-react";
import { customersApi, fileUrl } from "../../lib/api";

const ICONS = {
  create_estimate: FileText,
  add_estimate_items: FileText,
  convert_estimate_to_invoice: Receipt,
  email_document: Mail,
  create_followup_job: Briefcase,
  create_task: ListTodo,
  add_customer_note: StickyNote,
  update_job: Briefcase,
  add_customer_tag: Tag,
  message_teammate: MessageSquare,
  post_to_channel: Hash,
  book_appointment: CalendarClock,
};

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Confirm card for a Guru write/action (mirrors the Voice agent confirm UX).
 * For email_document with photos, shows an inline photo picker.
 */
export const GuruActionCard = ({ pending, onConfirm, onCancel, busy }) => {
  const { action, preview = {} } = pending || {};
  const Icon = ICONS[action] || Check;
  const [photos, setPhotos] = useState([]);
  const [selected, setSelected] = useState([]);

  const isEmail = action === "email_document";
  const customerId = preview.customerId;

  useEffect(() => {
    if (!isEmail || !preview.hasPhotos || !customerId) return;
    customersApi
      .get(customerId)
      .then((c) => setPhotos((c.photos || []).slice(0, 12)))
      .catch(() => setPhotos([]));
  }, [isEmail, preview.hasPhotos, customerId]);

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const confirm = () => onConfirm(isEmail ? { photoIds: selected } : {});

  return (
    <div
      data-testid={`guru-action-card-${action}`}
      className="mt-2 rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 to-blue-50 p-3.5 shadow-sm animate-in fade-in slide-in-from-bottom-1"
    >
      <div className="flex items-start gap-2.5">
        <div className="h-9 w-9 rounded-xl bg-white border border-violet-200 flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5 text-violet-600" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider font-bold text-violet-600">Confirm action</div>
          <div className="text-[14px] font-bold text-slate-900 leading-snug">{preview.label || action}</div>

          {/* Estimate / item list */}
          {Array.isArray(preview.items) && preview.items.length > 0 && (
            <div className="mt-2 rounded-lg bg-white border border-slate-200 divide-y divide-slate-100">
              {preview.items.map((it, i) => (
                <div key={i} className="flex items-center justify-between px-2.5 py-1.5 text-[12.5px]">
                  <span className="text-slate-700 truncate">
                    {it.qty} × {it.description}
                  </span>
                  <span className="font-semibold text-slate-900 shrink-0 ml-2">{money(it.qty * it.unitPrice)}</span>
                </div>
              ))}
              <div className="flex items-center justify-between px-2.5 py-1.5 text-[13px] font-bold bg-slate-50">
                <span>Total</span>
                <span>{money(preview.total)}</span>
              </div>
            </div>
          )}

          {/* Scalar previews */}
          {preview.total != null && !preview.items && (
            <div className="mt-1 text-[12.5px] text-slate-600">Total: <strong>{money(preview.total)}</strong></div>
          )}
          {preview.text && <div className="mt-1.5 text-[12.5px] text-slate-700 bg-white rounded-lg border border-slate-200 px-2.5 py-1.5 whitespace-pre-wrap">{preview.text}</div>}
          {preview.note && <div className="mt-1.5 text-[12.5px] text-slate-700 bg-white rounded-lg border border-slate-200 px-2.5 py-1.5 whitespace-pre-wrap">{preview.note}</div>}
          {preview.to && <div className="mt-1 text-[12px] text-slate-500">To: {preview.to}</div>}
          {preview.status && <div className="mt-1 text-[12px] text-slate-500">Status → {preview.status}</div>}
          {preview.dueAt && <div className="mt-1 text-[12px] text-slate-500">Due: {preview.dueAt}</div>}

          {/* Email photo picker */}
          {isEmail && photos.length > 0 && (
            <div className="mt-2">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 mb-1">
                <ImageIcon className="h-3.5 w-3.5" /> Attach photos {selected.length > 0 && <span className="text-violet-600">· {selected.length} selected</span>}
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {photos.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => toggle(p.id)}
                    data-testid={`guru-email-photo-${p.id}`}
                    className={`h-12 w-12 rounded-lg overflow-hidden border-2 transition-all ${
                      selected.includes(p.id) ? "border-violet-500 ring-2 ring-violet-200" : "border-slate-200"
                    }`}
                  >
                    <img src={fileUrl(p.url)} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={confirm}
              disabled={busy}
              data-testid="guru-action-confirm"
              className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[13px] font-bold flex items-center gap-1.5 disabled:opacity-60 active:scale-95 transition-all"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Confirm
            </button>
            <button
              onClick={onCancel}
              disabled={busy}
              data-testid="guru-action-cancel"
              className="h-9 px-3.5 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 text-[13px] font-medium flex items-center gap-1.5 disabled:opacity-60 active:scale-95 transition-all"
            >
              <X className="h-4 w-4" /> Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Result chip shown after an action succeeds. */
export const GuruResultCard = ({ result, onOpen }) => {
  if (!result) return null;
  return (
    <button
      onClick={() => onOpen?.(result)}
      data-testid="guru-result-card"
      className="mt-2 w-full text-left rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 flex items-center gap-2.5 hover:bg-emerald-100 transition-colors"
    >
      <div className="h-7 w-7 rounded-lg bg-white border border-emerald-200 flex items-center justify-center shrink-0">
        <Check className="h-4 w-4 text-emerald-600" />
      </div>
      <span className="text-[13px] font-semibold text-emerald-800 flex-1 truncate">{result.label}</span>
      <ExternalLink className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
    </button>
  );
};

/** Copyable payment-link / link card returned by read tools. */
export const GuruLinkCard = ({ card }) => {
  const [copied, setCopied] = useState(false);
  if (!card) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(card.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {}
  };
  return (
    <div data-testid="guru-link-card" className="mt-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 flex items-center gap-2.5">
      <div className="h-7 w-7 rounded-lg bg-white border border-blue-200 flex items-center justify-center shrink-0">
        <Link2 className="h-4 w-4 text-blue-600" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-semibold text-blue-800 truncate">{card.label}</div>
        <div className="text-[11px] text-blue-600/80 truncate">{card.url}</div>
      </div>
      <button
        onClick={copy}
        data-testid="guru-link-copy"
        className="h-8 px-2.5 rounded-lg bg-white border border-blue-200 hover:bg-blue-100 text-blue-700 text-[12px] font-semibold flex items-center gap-1.5 shrink-0"
      >
        <Copy className="h-3.5 w-3.5" /> {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
};

export default GuruActionCard;
